/**
 * Represents data for a particular item.
 * @typedef {Object} CartItemChangeData
 * @property {boolean} isNewVariant - Whether the variant is being replaced
 * @property {number} line - The line index.
 * @property {number} quantity - The quantity of the item.
 * @property {number} variant_id - The variant id
 * @property {number|null} selling_plan - The selling plan ID, or an empty string if not available.
 */

class CustomElement extends HTMLElement {
  constructor() {
    super();
  }

  /**
   *  Same as querySelector but ensure it is not null and the type is an HTMLElement
   *
   * @param {string} selector
   * @returns {HTMLElement}
   */
  qs(selector) {
    const element = this.qso(selector);
    if (!element) {
      throw new Error(
        `[QS ERROR] Expected element to exist for selector: ${selector}`,
      );
    }
    return element;
  }

  /**
   *  Same as querySelector but will return the element as an HTMLElement or null
   *
   * @param {string} selector
   * @returns {HTMLElement|null}
   */
  qso(selector) {
    return this.querySelector(selector);
  }

  /**
   * @param {string} selector
   * @return {NodeListOf<HTMLElement>}
   */
  qsa(selector) {
    return this.querySelectorAll(selector);
  }
}

class CartDrawer extends CustomElement {
  constructor() {
    super();

    /** @type {string|null} */
    this.nextFlyoutID = null;

    this.activeElement = null;
    this.form = this.qs('#CartDrawer-Form');

    this.addEventListener(
      'keyup',
      evt => evt.code === 'Escape' && this.close(),
    );

    this.qs('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this),
    );

    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.setHeaderCartIconAccessibility();
    this.checkURLParams();
    this.checkFreeGiftEligibility();
  }

  async checkFreeGiftEligibility() {
    const { gwp, cart } = this.getFreeGiftStatusAndCart();
    if (gwp) {
      await CartGWP.updateCartIfNeeded(gwp, cart);
    }
  }

  /**
   * @returns {{cart: Cart, gwp: ActiveGWP|null}}
   */
  getFreeGiftStatusAndCart() {
    const cart = JSON.parse(
      /** @ts-expect-error */
      this.querySelector('[data-name="cart"]').textContent,
    );

    /** @type {HTMLElement|null} */
    const gwpSettingsEl = this.querySelector('[data-name="settings"]');

    if (!gwpSettingsEl) {
      return { gwp: null, cart };
    }

    const gwpAmount = Number(gwpSettingsEl.dataset.amount);

    if (isNaN(gwpAmount) || gwpAmount === 0) {
      console.log('GWP Amount', gwpAmount);
      throw new Error('Invalid GWP amount');
    }

    /** @type {ActiveGWP} */
    const gwp = JSON.parse(gwpSettingsEl.textContent);
    // Override the base discount threshold with the local currency amount
    gwp.discount.threshold = gwpAmount;

    console.log('%cGWP', 'color:yellow;font-weight:bold', gwp);
    return { cart, gwp };
  }

  showConfetti() {
    if (this.dataset.gwpEnableConfetti !== 'true') {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    if ('confetti' in window) {
      const el = document.querySelector('.cart-drawer__inner');

      const width = document.body.clientWidth;
      const posX = width > 500 ? (width - 250) / width : 0.5;

      // @ts-expect-error
      confetti({
        particleCount: 100,
        spread: 70,
        container: this,
        zIndex: 2147483001,
        origin: { x: posX },
      });
    }
  }

  checkURLParams() {
    if (!window.location.search) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('viewcart') === 'true') {
      this.open();
    }
  }

  setHeaderCartIconAccessibility() {
    /** @type {HTMLElement} */
    // @ts-ignore
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', event => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', event => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  showFreeGiftDeletionWarning() {
    const configModal = document.querySelector('cart-gwp-delete-confirm-modal');
    configModal?.setAttribute('open', '');
  }

  hideFreeGiftDeletionWarning() {
    const configModal = document.querySelector('cart-gwp-delete-confirm-modal');
    configModal?.removeAttribute('open');
  }

  /**
   * @param {string|null} message
   */
  showCartError(message) {
    /** @type {HTMLElement} */
    // @ts-expect-error
    const textElement = this.querySelector('.cart-drawer__error-inner-text');

    if (!message) {
      this.classList.remove('has-error');
      textElement.textContent = '';
      const cartTotalSR = document.getElementById('cart-total-sr');
      cartTotalSR?.focus();
      return;
    }

    textElement.textContent = message;
    this.classList.add('has-error');

    const container = this.querySelector('cart-error');
    container?.focus();
  }

  log() {
    console.log.apply(console, [
      '%c[CART FLYOUT]',
      'color:yellow;font-weight:bold',
      ...arguments,
    ]);
  }

  clearCartError() {
    this.classList.remove('has-error');
    this.showCartError(null);
  }

  /**
   * @param {number} productId
   */
  setApplicableFlyoutUpsell(productId) {
    const existingSourceProduct = this.querySelector(
      `cart-item[data-product='${productId}']`,
    );

    if (existingSourceProduct) {
      this.nextFlyoutID = null;
      return;
    }

    const flyouts = [...this.querySelectorAll('cart-flyout')];
    const flyout = flyouts.find(
      el => Number(el.getAttribute('product')) === productId,
    );

    if (!flyout) {
      this.nextFlyoutID = null;
      return;
    }

    const relatedProduct = flyout.getAttribute('related-product');
    const relatedLineItem = this.querySelector(
      `cart-item[data-product='${relatedProduct}']`,
    );

    if (relatedLineItem) {
      this.nextFlyoutID = null;
      return;
    }

    const flyoutId = flyout.id;
    this.nextFlyoutID = flyoutId;
  }

  showCartFlyoutUpsell() {
    if (!this.nextFlyoutID) {
      return;
    }

    const flyout = this.querySelector(`cart-flyout[id=${this.nextFlyoutID}]`);
    this.nextFlyoutID = null;

    if (!flyout) {
      return;
    }

    window.setTimeout(() => {
      flyout.setAttribute('open', '');
    }, 100);
  }

  /**
   * @param {HTMLElement|null} [triggeredBy]
   */
  open(triggeredBy = null) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.clearCartError();
    this.style.display = '';
    this.showCartFlyoutUpsell();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        this.updateCartFocus();
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  updateCartFocus() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.cart-drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement =
      this.querySelector('.cart-drawer__inner') ||
      this.querySelector('.cart-drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    const flyouts = this.querySelectorAll('cart-flyout');
    for (const flyout of flyouts) {
      flyout.removeAttribute('open');
    }
    this.hideFreeGiftDeletionWarning();
  }

  /**
   * @param {Object} parsedState
   * @param {number} parsedState.id
   * @param {Record<string, string>} parsedState.sections
   * @param {boolean} [open]
   */
  async renderContents(parsedState, open = true) {
    /** @type {HTMLElement} */
    // @ts-ignore
    const inner = this.querySelector('.cart-drawer__inner');

    inner.classList.contains('is-empty') && inner.classList.remove('is-empty');
    this.productId = parsedState.id;

    // Calculate the applicable flyout before we actually update the section.
    this.setApplicableFlyoutUpsell(parsedState.product_id);

    /** @type {CartItems} */
    // @ts-expect-error
    const cartItems = this.querySelector('cart-items');

    cartItems.enableLoading(true);

    try {
      await cartItems.updateCartFromServerResponse(parsedState.sections);
    } finally {
      cartItems.disableLoading(true);
    }

    setTimeout(() => {
      // @ts-ignore
      this.querySelector('#CartDrawer-Overlay').addEventListener(
        'click',
        this.close.bind(this),
      );

      if (open) {
        this.open(null);
      }
    });
  }

  /**
   * @param {string} html
   * @param {string} [selector]
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return (
      new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
        ?.innerHTML || ''
    );
  }

  /**
   * @returns {Array<{id: string, selector?: string}>}
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  /**
   * @param {string} html
   * @param {string} [selector]
   * @returns {HTMLElement|null}
   */
  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  /**
   * @param {HTMLElement} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  onSubmit() {
    const checkoutBtn = this.qs('.cart-drawer__checkout-btn');
    checkoutBtn.classList.add('is-loading');
  }
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById(
      'CartDrawer-LineItemStatus',
    );

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]'),
    ).reduce(
      // @ts-ignore
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0,
    );

    /** @ts-expect-error */
    this.addEventListener('change', this.onChange.bind(this));

    /** @ts-expect-error */
    this.addEventListener('remove-bundle', this.onRemoveBundle.bind(this));

    /** @type {boolean} */
    this.loadingLock = false;
  }

  /** @param {CustomEvent<number[]>} event */
  onRemoveBundle(event) {
    this.deleteMultiple(event.detail, { _bundle: '' });
  }

  /**
   * @param {CustomEvent<CartItemChangeData>} event
   */
  onChange(event) {
    this.updateLineItem(
      event.detail.line,
      event.detail.quantity,
      event.detail.variant_id,
      event.detail.selling_plan,
      event.detail.isNewVariant,
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * @param {number} line
   */
  async deleteLineItem(line) {
    const body = JSON.stringify({
      line,
      quantity: 0,
    });
    await fetch(`${window.routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body },
    });
  }

  /**
   * @param {number[]} ids
   * @param {Record<string, string>} attributes
   */
  async deleteMultiple(ids, attributes) {
    this.enableLoading();

    const updates = ids.reduce((acc, id) => {
      // @ts-expect-error
      acc[id] = 0;
      return acc;
    }, {});

    const data = {
      updates,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: '/',
      attributes,
    };

    const body = JSON.stringify(data);
    return fetch('/cart/update.js', { ...fetchConfig(), ...{ body } })
      .then(res => res.json())
      .then(state => {
        this.updateCartFromServerResponse(state.sections);
      })
      .finally(() => this.disableLoading());
  }

  /**
   * @param {string} lineItemKey
   * */
  async removeFreeGift(lineItemKey) {
    const url = window.routes.cart_change_url;
    const data = {
      id: lineItemKey,
      quantity: 0,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: '/',
    };

    const body = JSON.stringify(data);

    await fetch(url, { ...fetchConfig(), ...{ body } })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          console.error('[GWP] Parsed state errors', parsedState.errors);
          return;
        }
        return this.updateCartFromServerResponse(parsedState.sections, false);
      })
      .catch(e => {
        console.error('[GWP] Error removing free gift from cart', e);
      })
      .finally(() => this.disableLoading());
  }

  /**
   *
   * @param {string} itemKey
   * @param {string} key
   * @param {string} value
   * */
  async addLineItemProperty(itemKey, key, value) {
    const url = window.routes.cart_change_url;
    const data = {
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: '/',
      id: itemKey,
      properties: {
        [key]: value,
      },
    };

    const body = JSON.stringify(data);
    await fetch(url, { ...fetchConfig(), ...{ body } })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          console.error(
            '[CART ITEM PROPERTY] Parsed state errors',
            parsedState.errors,
          );
          return;
        }
        return this.updateCartFromServerResponse(parsedState.sections, false);
      })
      .catch(e => {
        console.error('[CART ITEM] Error setting line item property', e);
      })
      .finally(() => this.disableLoading());
  }

  /**
   *
   * @param {string} key
   * @param {string} value
   * */
  async addCartAttribute(key, value) {
    const url = window.routes.cart_update_url;
    const data = {
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: '/',
      attributes: {
        [key]: value,
      },
    };

    const body = JSON.stringify(data);
    await fetch(url, { ...fetchConfig(), ...{ body } })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          console.error('[GWP] Parsed state errors', parsedState.errors);
          return;
        }
        return this.updateCartFromServerResponse(parsedState.sections);
      })
      .catch(e => {
        console.error(
          '[GWP] Error adding explicit opt out attribute to cart',
          e,
        );
      })
      .finally(() => this.disableLoading());
  }

  /**
   * @param {string} variantGid
   * @param {string} discountGid
   */
  async addFreeGift(variantGid, discountGid) {
    const variantId = Number(variantGid.split('/').pop());
    const discountId = Number(discountGid.split('/').pop());

    const url = window.routes.cart_add_url;
    const data = {
      id: variantId,
      quantity: 1,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: '/',
      properties: {
        _freegift: discountId,
      },
    };

    const body = JSON.stringify(data);

    await fetch(url, { ...fetchConfig(), ...{ body } })
      .then(response => response.text())
      .then(async state => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          console.error('[GWP] Parsed state errors', parsedState.errors);
          return;
        }
        await this.updateCartFromServerResponse(parsedState.sections);

        /** @type {CartDrawer} */
        // @ts-expect-error
        const cart = document.querySelector('cart-drawer');
        cart.showConfetti();
      })
      .catch(e => {
        console.error('[GWP] Error adding free gift to cart', e);
      })
      .finally(() => this.disableLoading());
  }

  /**
   * @param {number|undefined} line
   * @param {number} quantity
   * @param {number} variantId
   * @param {number|null} sellingPlanId
   * @param {boolean} isNewVariant
   */
  async updateLineItem(line, quantity, variantId, sellingPlanId, isNewVariant) {
    this.enableLoading(true);

    let url = window.routes.cart_change_url;

    /** @type {any} */
    const data = {
      line: line,
      quantity,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: '/',
      selling_plan: sellingPlanId,
    };

    // TOODO: Refactor so w can easily add to cart, delete, etc.
    if (isNewVariant) {
      // Hack for now
      if (line !== undefined) {
        await this.deleteLineItem(line);
      }
      data.line = undefined;
      data.id = variantId;
      url = window.routes.cart_add_url;
    }

    const body = JSON.stringify(data);

    fetch(url, { ...fetchConfig(), ...{ body } })
      .then(response => {
        return response.text();
      })
      .then(async state => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          // If it's a new variant we have to re-add it to the previous state (ie qty+plan)
          // and show an error differently.
          if (isNewVariant) {
            this.restoreItemAndShowError(line, parsedState.errors).then(() => {
              this.disableLoading(true);
            });
            return;
          }

          const quantityElement = document.getElementById(
            `Drawer-quantity-${line}`,
          );
          quantityElement.value = quantityElement.getAttribute('value');

          /** @type {CartDrawer} */
          // @ts-expect-error
          const cartDrawer = document.querySelector('cart-drawer');
          cartDrawer.showCartError(parsedState.errors);
          this.disableLoading(true);

          return;
        }

        await this.updateCartFromServerResponse(parsedState.sections, true);
        this.disableLoading(true);

        if (!line) {
          /** @type {CartDrawer} */
          // @ts-expect-error
          const cartDrawer = document.querySelector('cart-drawer');
          cartDrawer.showCartFlyoutUpsell();
        }
      })
      .catch(e => {
        console.error('[CART UPDATE ERROR]', e);
        this.querySelectorAll('.loading-overlay').forEach(overlay =>
          overlay.classList.add('hidden'),
        );
        // @ts-ignore
        document.getElementById('cart-errors').textContent =
          window.cartStrings.error;
        this.disableLoading(true);
      });
  }

  /**
   * @param {Record<string, string>} sections
   * @param {boolean} [checkFreeGift]
   */
  async updateCartFromServerResponse(sections, checkFreeGift = true) {
    this.getSectionsToRender().forEach(section => {
      /** @type {HTMLElement} */
      // @ts-ignore
      const elementToReplace =
        // @ts-ignore
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(
        sections[section.section],
        section.selector,
      );
    });

    this.enableLoading();

    if (checkFreeGift) {
      /** @type {CartDrawer} */
      // @ts-expect-error
      const cart = document.querySelector('cart-drawer');
      await cart.checkFreeGiftEligibility();
    }
  }

  /**
   * @param {number} line
   * @param {string} message
   */
  async restoreItemAndShowError(line, message) {
    /** @type {CartDrawer} */
    // @ts-expect-error
    const cartDrawer = document.querySelector('cart-drawer');

    /** @type {HTMLElement} */
    // @ts-expect-error
    const cartItem = this.querySelector(`cart-item[index="${line}"]`);

    let qty = Number(cartItem.dataset.originalQuantity);
    const variantId = Number(cartItem.dataset.originalVariantId);
    const sellingPlanId = cartItem.dataset.originalSellingPlanId
      ? Number(cartItem.dataset.originalSellingPlanId)
      : null;

    const url = window.routes.cart_add_url;
    const data = {
      id: variantId,
      quantity: qty,
      selling_plan: sellingPlanId,
    };

    const body = JSON.stringify(data);
    try {
      await fetch(url, { ...fetchConfig(), ...{ body } });
    } catch (e) {
      console.error('Error restoring cart item', e);
    }
    await this.refreshCart();
    cartDrawer.showCartError(message);
  }

  async refreshCart() {
    const sections = this.getSectionsToRender().map(section => section.section);
    const params = 'sections=' + sections.join(',');

    const response = await fetch(`/?${params}`, {
      ...fetchConfig(),
      method: 'GET',
    });
    const state = await response.json();
    await this.updateCartFromServerResponse(state);
  }

  /**
   * @param {number} line
   * @param {string} message
   */
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector('.cart-item__error-text').textContent =
        message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * @param {string} html
   * @param {string} selector
   * @returns {string}
   */
  getSectionInnerHTML(html, selector) {
    // @ts-ignore
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  /**
   * @param {boolean} [lock]
   */
  enableLoading(lock = false) {
    if (lock && this.loadingLock) {
      console.error('Loading state already locked, default to normal');
      this.loadingLock = false;
    }

    if (this.loadingLock) {
      return;
    }

    if (lock) {
      /** @type {HTMLElement} */
      // @ts-expect-error
      const cartInner = document.querySelector('.cart-drawer__inner');

      /** @type {HTMLElement} */
      // @ts-expect-error
      const clonedInner = cartInner.cloneNode(true);
      clonedInner.classList.add('is-clone');
      cartInner.insertAdjacentElement('afterend', clonedInner);
      this.loadingLock = true;
    }

    /** @type {HTMLElement} */
    // @ts-ignore
    const cart = document.querySelector('cart-drawer');

    cart
      .querySelectorAll('.cart-drawer__loader')
      .forEach(l => l.classList.add('is-active'));
  }

  disableLoading(unlock = false) {
    if (unlock && !this.loadingLock) {
      console.error('Unlock called without lock, default to normal');
    }

    if (!unlock && this.loadingLock) {
      return;
    }

    if (unlock) {
      this.loadingLock = false;

      /** @type {CartGwpProgressBar|null} */
      const progressBar = document.querySelector('cart-gwp-progress-bar');

      /** @type {CartGwpProgressBar|null} */
      const clonedProgressBar = document.querySelector(
        '.cart-drawer__inner.is-clone cart-gwp-progress-bar',
      );

      if (progressBar && clonedProgressBar) {
        const currentProgress = progressBar.getProgress();
        const previousProgress = clonedProgressBar.getProgress();

        progressBar.setImmediateProgress(previousProgress, false);

        document.querySelector('.cart-drawer__inner.is-clone')?.remove();

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            progressBar.setImmediateProgress(currentProgress, true);
          });
        });
      } else {
        document.querySelector('.cart-drawer__inner.is-clone')?.remove();
      }
    }

    /** @type {HTMLElement} */
    // @ts-ignore
    const cart = document.querySelector('cart-drawer');

    cart
      .querySelectorAll('.cart-drawer__loader')
      .forEach(l => l.classList.remove('is-active'));

    const cartTotalSR = document.getElementById('cart-total-sr');
    cartTotalSR?.focus();
  }
}

class CartItem extends CustomElement {
  constructor() {
    super();
    this.subscriptionButton = this.qso('.cart-item__subscription-btn');
    this.removeButton = this.qs('.cart-item__remove-btn');

    this.handleChange = this.handleChange.bind(this);
    this.handleClickRemove = this.handleClickRemove.bind(this);

    this.handleSubscriptionButtonClick =
      this.handleSubscriptionButtonClick.bind(this);

    this.addEventListener('change', this.handleChange);
    this.removeButton.addEventListener('click', this.handleClickRemove);

    if (this.subscriptionButton) {
      this.subscriptionButton.addEventListener(
        'click',
        this.handleSubscriptionButtonClick,
      );
    }
  }

  /**
   * @param {Event} e
   */
  handleClickRemove(e) {
    e.preventDefault();

    if (this.dataset.bundle === 'true') {
      const ids = this.dataset.itemIds?.split(',').filter(Boolean).map(Number);
      this.dispatchEvent(
        new CustomEvent('remove-bundle', {
          bubbles: true,
          detail: ids,
        }),
      );
      return;
    }

    if (this.dataset.fg === 'true') {
      /** @type {CartDrawer} */
      // @ts-expect-error
      const drawer = document.querySelector('cart-drawer');
      drawer.showFreeGiftDeletionWarning();
      return;
    }

    /** @type {CartItemChangeData} */
    const data = {
      isNewVariant: true,
      line: Number(this.getAttribute('index')),
      quantity: 0,
      variant_id: Number(this.getAttribute('variant-id')),
      selling_plan: null,
    };

    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        detail: data,
      }),
    );
  }

  /**
   * @param {Event} e
   */
  handleSubscriptionButtonClick(e) {
    e.preventDefault();

    const index = Number(this.getAttribute('index'));
    // @ts-ignore
    const qty = Number(this.querySelector('.quantity__input').value);

    // @ts-ignore
    const planId = Number(this.subscriptionButton.dataset.planId);

    // @ts-ignore
    const variantId = Number(this.subscriptionButton.dataset.variantId);

    /** @type {CartItemChangeData} */
    const data = {
      isNewVariant: true,
      line: index,
      quantity: qty,
      variant_id: variantId,
      selling_plan: planId,
    };

    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        detail: data,
      }),
    );
  }

  /**
   * @param {Event} e
   */
  handleChange(e) {
    /** @type {HTMLElement} */
    // @ts-ignore
    const target = e.target;

    if (target.nodeName === 'CART-ITEM') {
      return;
    }

    /** @type {HTMLElement|null} */
    const subscriptionSelect = this.querySelector('subscription-select');
    const index = Number(this.getAttribute('index'));

    // @ts-ignore
    const qty = Number(this.querySelector('.quantity__input').value);

    let originalVariantId = Number(this.getAttribute('variant-id'));
    let planId = null;
    let variantId = originalVariantId;

    if (subscriptionSelect) {
      const value = subscriptionSelect.getAttribute('plan');
      if (value && value !== 'onetime') {
        planId = value;
      }
      variantId = Number(subscriptionSelect.getAttribute('variant'));
    }

    const isNewVariant = variantId !== originalVariantId;

    /** @type {CartItemChangeData} */
    const data = {
      isNewVariant: isNewVariant,
      line: index,
      quantity: qty,
      variant_id: variantId,
      selling_plan: planId ? Number(planId) : null,
    };

    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        detail: data,
      }),
    );

    e.stopPropagation();
  }
}

class CartFlyout extends CustomElement {
  constructor() {
    super();
    this.btnAccept = this.qs('.cart-flyout__action-atc-yes');
    this.btnDecline = this.qs('.cart-flyout__action-atc-no');
    this.closeButton = this.qs('.cart-flyout__close-btn');

    this.btnAccept.addEventListener(
      'click',
      this.onAcceptButtonClick.bind(this),
    );

    this.btnDecline.addEventListener(
      'click',
      this.onDeclineButtonClick.bind(this),
    );

    this.closeButton.addEventListener(
      'click',
      this.onCloseButtonClick.bind(this),
    );
  }

  static get observedAttributes() {
    return ['open'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'open') {
      return;
    }

    if (newValue === '') {
      this.slideIn();
    } else {
      this.setAttribute('inert', '');
    }
  }

  slideIn() {
    this.removeAttribute('inert');
    this.trackFlyoutPresented();
    this.ariaHideSiblings();
    setTimeout(() => {
      trapFocus(this, this.closeButton);
    }, 500);
  }

  ariaHideSiblings() {
    /** @type {HTMLCollectionOf<HTMLElement>} */
    /** @ts-expect-error */
    const children = this.parentElement.children;

    for (const child of children) {
      if (child !== this) {
        child.setAttribute('aria-hidden', 'true');
      }
    }
  }

  ariaShowSiblings() {
    /** @type {HTMLCollectionOf<HTMLElement>} */
    /** @ts-expect-error */
    const children = this.parentElement.children;

    for (const child of children) {
      if (child !== this) {
        child.removeAttribute('aria-hidden');
      }
    }
  }

  slideOut() {
    this.removeAttribute('open');
    this.ariaShowSiblings();
    window.setTimeout(() => {
      /** @type {CartDrawer} */
      /* @ts-expect-error */
      const drawer = document.querySelector('cart-drawer');
      drawer.updateCartFocus();
    }, 500);
  }

  onAcceptButtonClick() {
    this.setAttribute('loading', '');

    /** @type {CartItems} */
    // @ts-expect-error
    const cartItems = document.querySelector('cart-items');
    const variantId = Number(this.getAttribute('related-variant'));
    cartItems.updateLineItem(undefined, 1, variantId, null, true);

    window.setTimeout(() => {
      /** @type {CartDrawer} */
      /* @ts-expect-error */
      const drawer = document.querySelector('cart-drawer');
      drawer.updateCartFocus();
    }, 100);

    this.trackFlyoutAccepted();
  }

  onDeclineButtonClick() {
    this.slideOut();
    this.trackFlyoutRejected();
  }

  onCloseButtonClick() {
    this.slideOut();
    this.trackFlyoutRejected();
  }

  trackFlyoutPresented() {
    const data = {
      product_id: this.getAttribute('product') || '',
      related_product: this.getAttribute('related-product') || '',
    };
    window.Shopify.analytics.publish('cart_flyout_presented', data);
  }

  trackFlyoutAccepted() {
    const data = {
      product_id: this.getAttribute('product') || '',
      related_product: this.getAttribute('related-product') || '',
    };
    window.Shopify.analytics.publish('cart_flyout_accepted', data);
  }

  trackFlyoutRejected() {
    const data = {
      product_id: this.getAttribute('product') || '',
      related_product: this.getAttribute('related-product') || '',
    };
    window.Shopify.analytics.publish('cart_flyout_rejected', data);
  }
}

class CartUpsellItem extends CustomElement {
  constructor() {
    super();
    this.addBtn = this.qs('button[data-action="add-to-cart"]');
    this.addBtn.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick() {
    if (this.addBtn.getAttribute('data-state') === 'loading') {
      return;
    }

    const variantId = Number(this.getAttribute('variant-id'));
    const productId = Number(this.getAttribute('product-id'));
    const cartItems = document.querySelector('cart-items');

    /** @type {CartDrawer} */
    // @ts-expect-error
    const cart = document.querySelector('cart-drawer');
    cart.setApplicableFlyoutUpsell(productId);

    // @ts-ignore
    cartItems.updateLineItem(undefined, 1, variantId, null, true);

    const item_name = this.getAttribute('title');
    const dLeventName = 'quick_add_to_cart';
    const purchaseType = 'one-time';
    const atcType = 'cart quick add';
    const quantity = 1;
    const dataLayer = (window.dataLayer = window.dataLayer || []);
    dataLayer.push({
      event: dLeventName,
      purchaseType,
      atcType,
      ecommerce: {
        items: [
          {
            item_name,
            quantity,
          },
        ],
      },
    });
  }
}

class SubscriptionSelect extends CustomElement {
  constructor() {
    super();

    this.trigger = this.qs('.subscription-select__label');
    this.triggerInner = this.qs('.subscription-select__label-text-inner');
    this.itemsContainer = this.qs('.subscription-select__items');
    this.selectItems = this.qsa('.subscription-select__item');

    this.handleToggle = this.handleToggle.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.trigger.addEventListener('click', this.handleToggle);
    this.addEventListener('keydown', this.handleKeyDown);
  }

  open() {
    const value = this.getAttribute('plan');

    // Hide the selected item from the list
    this.selectItems.forEach(item => {
      if (item.dataset.planId === value) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
      }
    });

    this.selectItems.forEach(item => {
      item.addEventListener('click', this.handleSelect);
    });

    this.setAttribute('state', 'open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.focusFirstOrSelectedItem();
    document.body.addEventListener('click', this.handleClickOutside);
  }

  close() {
    this.setAttribute('state', 'closed');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.addEventListener(
      'animationend',
      () => {
        this.setAttribute('state', 'idle');
      },
      { once: true },
    );

    this.selectItems.forEach(item => {
      item.removeEventListener('click', this.handleSelect);
    });
    document.body.removeEventListener('click', this.handleClickOutside);
  }

  getFirstVisibleItem() {
    /** @type {NodeListOf<HTMLElement>} */
    const items = this.querySelectorAll('[role="option"]');
    for (const item of items) {
      if (item.style.display !== 'none') {
        return item;
      }
    }
    return null;
  }

  focusFirstOrSelectedItem() {
    let item = this.querySelector("[role='option'][aria-selected='true']");

    if (item == null || item.style.display === 'none') {
      item = this.getFirstVisibleItem();
    }

    item?.focus();
  }

  handleClickOutside() {
    this.close();
  }

  /**
   * @param {MouseEvent} e
   */
  handleToggle(e) {
    e.stopPropagation();
    e.preventDefault();
    if (this.getAttribute('state') === 'open') {
      this.close();
    } else {
      this.open();
      this.focusFirstOrSelectedItem();
    }
  }

  isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    /** @type {HTMLElement} */
    // @ts-ignore
    const target = event.target;

    if (this.isOpen() && event.key === 'Escape') {
      event.stopImmediatePropagation();
      this.close();
      this.trigger.focus();
      return;
    }

    if (target === this.trigger) {
      if (!this.isOpen() && (event.key === 'Enter' || event.key === ' ')) {
        this.open();
        this.focusFirstOrSelectedItem();
        event.preventDefault();
        return;
      }

      if (this.isOpen() && (event.key === 'Enter' || event.key === ' ')) {
        this.close();
        this.focus();
        event.preventDefault();
        return;
      }
      if (this.isOpen() && event.key === 'Tab') {
        event.preventDefault();
        return;
      }
    }

    if (target.matches('[role="option"]')) {
      if (event.key === 'Tab') {
        event.preventDefault();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        this.selectItem(target);
        this.close();
        event.preventDefault();
        this.trigger.focus();
        return;
      }

      if (event.key === 'ArrowUp') {
        const [prevItem, _nextItem] = this.getPrevAndNextItemCandidates(target);
        if (prevItem) {
          prevItem.focus();
        } else {
          this.close();
          this.trigger.focus();
        }
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowDown') {
        const [_prevItem, nextItem] = this.getPrevAndNextItemCandidates(target);
        if (nextItem) {
          nextItem.focus();
        }
        event.preventDefault();
        return;
      }
    }
  }

  /** @param {HTMLElement} item */
  getPrevAndNextItemCandidates(item) {
    /** @type {HTMLElement|null} */
    // @ts-expect-error
    let nextItem = item.nextElementSibling;

    /** @type {HTMLElement|null} */
    // @ts-expect-error
    let prevItem = item.previousElementSibling;

    while (nextItem) {
      if (nextItem.style.display !== 'none') {
        break;
      }
      // @ts-expect-error
      nextItem = nextItem.nextElementSibling;
    }

    while (prevItem) {
      if (prevItem.style.display !== 'none') {
        break;
      }

      // @ts-expect-error
      prevItem = prevItem.previousElementSibling;
    }
    return [prevItem, nextItem];
  }

  /**
   * @param {MouseEvent} e
   */
  handleSelect(e) {
    e.stopPropagation();

    /** @type {HTMLElement} */
    // @ts-ignore
    const item = e.currentTarget;
    this.selectItem(item);
  }

  /**
   * @param {HTMLElement} item
   */
  selectItem(item) {
    this.triggerInner.innerHTML = item.innerHTML;
    this.setAttribute('plan', item.dataset.planId || '');
    this.setAttribute('variant', item.dataset.variantId || '');
    this.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  }
}

class CartSmsWidget extends CustomElement {
  constructor() {
    super();
    /** @type {HTMLInputElement} */
    // @ts-ignore
    this.checkbox = this.querySelector('.cart-sms-widget__cb');

    this.checkbox.checked = this.getRealCheckboxChecked();
    this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this));

    this.onCheckboxChange();
  }

  getRealCheckboxChecked() {
    /** @type {HTMLInputElement|null} */
    const rechargeCheckbox = document.querySelector(
      '.rcsms-cart-widget__checkbox',
    );

    if (rechargeCheckbox) {
      return rechargeCheckbox.checked;
    }

    // Recharge SMS always defaults to checked on page load for some reason.
    return true;
  }

  onCheckboxChange() {
    /** @type {HTMLInputElement|null} */
    const rechargeCheckbox = document.querySelector(
      '.rcsms-cart-widget__checkbox',
    );

    if (!rechargeCheckbox) {
      return;
    }

    const event = new Event('click', { bubbles: true });
    rechargeCheckbox.checked = this.checkbox.checked;
    rechargeCheckbox.dispatchEvent(event);
  }
}

class CartError extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLElement} */
    // @ts-expect-error
    this.closeButton = this.querySelector('.cart-drawer__error-close');

    /** @type {CartDrawer|null} */
    this.cartDrawer = document.querySelector('cart-drawer');

    // @ts-expect-error
    this.closeButton.addEventListener(
      'click',
      this.onCloseButtonClick.bind(this),
    );
  }

  onCloseButtonClick() {
    if (this.cartDrawer) {
      this.cartDrawer.clearCartError();
    }
  }
}

class CartGwpDeleteConfirmModal extends HTMLElement {
  static observedAttributes = ['open'];

  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    /** @type {HTMLDialogElement} */
    // @ts-expect-error
    this.dialogEl = this.querySelector('dialog');
  }

  connectedCallback() {
    /** @type {NodeListOf<HTMLElement>} */
    const buttons = this.querySelectorAll('button[data-action]');
    buttons.forEach(button => button.addEventListener('click', this.onClick));
  }

  disconnectedCallback() {
    /** @type {NodeListOf<HTMLElement>} */
    const buttons = this.querySelectorAll('button[data-action]');
    buttons.forEach(button =>
      button.removeEventListener('click', this.onClick),
    );
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open' && oldValue !== newValue) {
      if (newValue === '') {
        this.#showDialog();
      } else if (newValue === null) {
        this.#hideDialog();
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  onClick(event) {
    /** @type {HTMLElement} */
    // @ts-ignore
    const target = event.currentTarget;

    if (target.dataset.action === 'ok') {
      this.removeAttribute('open');
      CartGWP.explicitRemoveFreeGift();
    } else if (target.dataset.action === 'cancel') {
      this.removeAttribute('open');
    } else if (target.dataset.action === 'close') {
      this.removeAttribute('open');
    }
  }

  #showDialog() {
    this.dialogEl.setAttribute('open', '');
  }

  #hideDialog() {
    this.removeAttribute('open');
    this.dialogEl.removeAttribute('open');
  }
}

class CartGwpProgressBar extends HTMLElement {
  /** @type {number|null} */
  static lastPercentage = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (CartGWP.isExplicitlyRemoved()) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';
    this.render();
  }

  /**
   * @returns {number}
   */
  getProgress() {
    return +this.style
      .getPropertyValue('--progress-bar-width')
      .replace('%', '');
  }

  /**
   * @param {number} percentage A value between 0 and 100
   */
  setImmediateProgress(percentage, animate = false) {
    /** @type {HTMLElement} */
    // @ts-expect-error
    const fillBar = this.querySelector('.cart-drawer__gwp-progress-bar-fill');

    if (!animate) {
      fillBar.style.transition = 'none';
    }

    if (percentage >= 100) {
      this.setAttribute('view', 'complete');
    } else {
      this.setAttribute('view', 'incomplete');
    }

    this.style.setProperty('--progress-bar-width', `${percentage.toFixed(2)}%`);

    if (!animate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fillBar.style.transition = '';
        });
      });
    }
  }

  async render() {
    /** @type {CartDrawer} */
    // @ts-expect-error
    const drawer = document.querySelector('cart-drawer');
    const { gwp, cart } = drawer.getFreeGiftStatusAndCart();

    if (gwp == null) {
      return;
    }

    const status = CartGWP.getStatus(gwp, cart);

    /** @ts-expect-error */
    this.querySelector('[data-remaining]').textContent = Shopify.formatMoney(
      status.remaining,
    );

    this.setImmediateProgress(status.percentage, true);
  }
}

customElements.define('subscription-select', SubscriptionSelect);
customElements.define('cart-item', CartItem);
customElements.define('cart-upsell-item', CartUpsellItem);
customElements.define('cart-flyout', CartFlyout);
customElements.define('cart-items', CartItems);
customElements.define('cart-drawer', CartDrawer);
customElements.define('cart-sms-widget', CartSmsWidget);
customElements.define('cart-error', CartError);
customElements.define(
  'cart-gwp-delete-confirm-modal',
  CartGwpDeleteConfirmModal,
);
customElements.define('cart-gwp-progress-bar', CartGwpProgressBar);
